import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { Box, Link, Spinner, Text } from "@chakra-ui/react";
import { Duration } from "@js-joda/core";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import type { ResponseOf } from "../../../../../core/api";
import useApi from "../../../../../shared/hooks/useApi";
import type { CaregiverId } from "../../../../../shared/schema/schema";
import { currencyFormatter } from "../../../../../shared/utils/currency-formatter";
import { dateFormatter } from "../../../../../shared/utils/date-formatter";
import EntityProfileDataTable, {
  type EntityProfileDataTableColumn,
} from "../../../../entityProfileModal/components/EntityProfileDataTable";
import type { CaregiverProfileBridgeActions } from "../../../caregiverProfileModal.actions";

type PayrollResponse = ResponseOf<"get", "./caregivers/:caregiverId/payroll">;
type PayrollBatch = PayrollResponse["payrollBatchs"][number];
type PayrollItem = PayrollBatch["payrollItems"][number];
type PayrollBatchSortKey = "batchId" | "date";
type PayrollItemSortKey =
  | "date"
  | "endTime"
  | "hours"
  | "overtime"
  | "patient"
  | "rate"
  | "startTime"
  | "total"
  | "type";

const payrollTypeLabels: Record<PayrollItem["type"], string> = {
  IN_CLASS: "In Class",
  ONLINE_TRAINING: "Online Training",
  PTO: "PTO",
  SOH: "SOH",
  TRAVEL_TIME: "Travel Time",
  VISIT: "Visit",
};

type Props = {
  actions: CaregiverProfileBridgeActions;
  caregiverId: CaregiverId;
};

export default function CaregiverProfilePayrollSection(props: Props) {
  const { api } = useApi();
  const payrollQuery = useQuery({
    queryKey: ["caregiverProfileModal", props.caregiverId, "payroll"],
    queryFn: () =>
      api.get("./caregivers/:caregiverId/payroll", {
        path: { caregiverId: props.caregiverId },
      }),
  });
  const [expandedBatchIds, setExpandedBatchIds] = React.useState<Set<number>>(new Set());
  const payrollBatches = payrollQuery.data?.payrollBatchs ?? [];

  const batchColumns = React.useMemo(
    (): Array<EntityProfileDataTableColumn<PayrollBatch, PayrollBatchSortKey>> => [
      {
        key: "batchId",
        header: "Batch ID",
        render: (row) => (
          <Text as="span" fontWeight={600}>
            {row.payrollBatch.displayId}
          </Text>
        ),
        sortKey: "batchId",
        sortValue: (row) => row.payrollBatch.displayId,
      },
      {
        key: "date",
        header: "Payroll Date",
        render: (row) => formatDate(row.payrollBatch.date),
        sortKey: "date",
        sortValue: (row) => getSortValue(row.payrollBatch.date),
      },
    ],
    []
  );

  if (payrollQuery.isLoading) {
    return (
      <Text color="gray.600" fontSize="14px">
        Loading payroll...
      </Text>
    );
  }

  if (payrollBatches.length === 0) {
    return (
      <Text fontSize="22px" fontWeight={400}>
        Caregiver has no payroll items
      </Text>
    );
  }

  return (
    <Box>
      {payrollQuery.isFetching ? <Spinner mb={2} size="sm" /> : null}
      <EntityProfileDataTable
        columns={batchColumns}
        defaultPageSize={10}
        defaultSort={{ key: "date", direction: "desc" }}
        getRowKey={(row) => row.payrollBatch.id}
        pageSizeOptions={[10, 25, 50, 100]}
        renderExpandedRow={(row) =>
          expandedBatchIds.has(Number(row.payrollBatch.id)) ? (
            <PayrollItemsRow actions={props.actions} items={row.payrollItems} />
          ) : null
        }
        rowProps={(row) => ({
          cursor: "pointer",
          onClick: () => {
            setExpandedBatchIds((current) => {
              const next = new Set(current);
              const batchId = Number(row.payrollBatch.id);

              next.has(batchId) ? next.delete(batchId) : next.add(batchId);

              return next;
            });
          },
        })}
        rows={payrollBatches}
        showEmptyState={false}
        tableSx={{
          "& th": { fontWeight: 600, textAlign: "left" },
          "& td": { fontSize: "14px" },
        }}
      />
    </Box>
  );
}

function PayrollItemsRow(props: { actions: CaregiverProfileBridgeActions; items: PayrollItem[] }) {
  const itemColumns = React.useMemo(
    (): Array<EntityProfileDataTableColumn<PayrollItem, PayrollItemSortKey>> => [
      {
        key: "patient",
        header: "Patient",
        render: (row) => {
          const patientId = row.patientId;

          return patientId === null ? null : (
            <Link
              color="#337ab7"
              textDecoration="underline"
              onClick={(event) => {
                event.stopPropagation();
                props.actions.navigateToPatient(patientId);
              }}
            >
              {getPayrollItemPatientLabel(row)}
            </Link>
          );
        },
        sortKey: "patient",
        sortValue: (row) => getPayrollItemPatientLabel(row),
      },
      {
        key: "date",
        header: "Date",
        render: (row) => formatDate(row.startTime),
        sortKey: "date",
        sortValue: (row) => getSortValue(row.startTime),
      },
      {
        key: "startTime",
        header: "Start Time",
        render: (row) => formatTime(row.startTime),
        sortKey: "startTime",
        sortValue: (row) => getSortValue(row.startTime),
      },
      {
        key: "endTime",
        header: "End Time",
        render: (row) => formatTime(row.endTime),
        sortKey: "endTime",
        sortValue: (row) => getSortValue(row.endTime),
      },
      {
        key: "type",
        header: "Type",
        render: (row) => payrollTypeLabels[row.type],
        sortKey: "type",
        sortValue: (row) => payrollTypeLabels[row.type],
      },
      {
        key: "rate",
        header: "Rate",
        render: (row) => currencyFormatter.formatCents(row.rate),
        sortKey: "rate",
        sortValue: (row) => row.rate,
      },
      {
        key: "total",
        header: "Total Amount",
        render: (row) => currencyFormatter.formatCents(row.total),
        sortKey: "total",
        sortValue: (row) => row.total,
      },
      {
        key: "hours",
        header: "Hours Amount",
        render: (row) => formatSeconds(row.seconds),
        sortKey: "hours",
        sortValue: (row) => row.seconds,
      },
      {
        key: "overtime",
        header: "Overtime?",
        render: (row) =>
          row.overtime ? (
            <CheckIcon color="blue.500" />
          ) : (
            <CloseIcon boxSize={3} color="gray.700" />
          ),
        sortKey: "overtime",
        sortValue: (row) => Number(row.overtime),
      },
    ],
    [props.actions]
  );

  return (
    <tr>
      <td colSpan={2} style={{ padding: "16px 0 0" }}>
        <Box maxW="100%" overflowX="auto" px={3}>
          <EntityProfileDataTable
            columns={itemColumns}
            defaultPageSize={25}
            defaultSort={{ key: "date", direction: "desc" }}
            getRowKey={(row) => `${row.type}-${row.id}`}
            minW="900px"
            rowProps={(row) => ({
              cursor: row.type === "VISIT" && row.visitInstanceId !== null ? "pointer" : "default",
              onClick: () => {
                if (row.type === "VISIT" && row.visitInstanceId !== null) {
                  props.actions.navigateToVisitInstance(row.visitInstanceId);
                }
              },
            })}
            rows={props.items}
            showEmptyState={false}
            tableSx={{
              "& th": { fontWeight: 600, textAlign: "left" },
              "& td": { fontSize: "14px" },
            }}
          />
        </Box>
      </td>
    </tr>
  );
}

function formatDate(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  try {
    return dateFormatter.toDate(value as never);
  } catch {
    return String(value);
  }
}

function formatTime(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  try {
    return dateFormatter.toTime(value as never);
  } catch {
    return "";
  }
}

function getSortValue(value: unknown) {
  return value === null || value === undefined ? "" : String(value);
}

function getPayrollItemPatientLabel(row: PayrollItem) {
  const displayData = row as PayrollItem & {
    patientDisplayId?: number | null;
    patientDisplayName?: string | null;
    patientName?: string | null;
  };
  const name = displayData.patientDisplayName ?? displayData.patientName;
  const displayId = displayData.patientDisplayId;

  if (name !== null && name !== undefined && name !== "") {
    return displayId === null || displayId === undefined ? name : `${name} (${displayId})`;
  }

  return row.patientId === null ? "" : `Patient ${row.patientId}`;
}

function formatSeconds(seconds: number | null | undefined) {
  if (seconds === null || seconds === undefined) {
    return "";
  }

  const sign = seconds < 0 ? "-" : "";
  const duration = Duration.ofSeconds(Math.abs(seconds));
  const hours = Math.floor(duration.toHours());
  const minutes = Math.floor(duration.minusHours(hours).toMinutes());

  return `${sign}${hours}:${String(minutes).padStart(2, "0")} hr`;
}
